import { useFilterLabels } from "@/components/organisms/Filters/PlayerFilters/filterLabels";
import { GAME_ASSET_SLUG } from "@/config/game";
import { DropdownItem } from "@/design-system/Dropdown/Dropdown";
import { TextButton } from "@/design-system/TextButton/TextButton";
import {
  contentLabelsSelector,
  loadedContentSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { playStyleCategoryNamesSelector } from "@/redux/players/playersSlice";
import { inGameAttributeCategories } from "@/redux/squadBuilder/squadBuilderUtils";
import { useTranslation } from "@repo/shared/i18n";
import {
  categoryToUniqueKey,
  cmToFeetAndInches,
  inGameAttributesDict,
} from "@repo/shared/utils";
import { BarChart3, Info } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  ExpandButton,
  FilterCard,
  NestedToggleRow,
  NumberInput,
  PickerRow,
  RangeInputs,
  SegmentRow,
  ValueRow,
} from "./FilterPrimitives";
import {
  RangeFilterType,
  SelectorFilterType,
  UpdateFilterType,
} from "./filterTypes";

const ASSETS = import.meta.env.VITE_APP_ASSETS_BASE_URL;
const asset = (path: string) => `${ASSETS}/${GAME_ASSET_SLUG}/${path}`;

type Filters = (SelectorFilterType | RangeFilterType)[];

type Props = {
  filters: Filters;
  activeCount: number;
  onUpdateFilter: (f: UpdateFilterType) => void;
  onClearFilter: (key: string) => void;
  onClearAll: () => void;
  /** "2 active · 1,420+ players", or "No filters applied". */
  summary: string;
  /** Replaces the summary line, e.g. the phone panel's chips row. */
  chips?: ReactNode;
  /** Filter keys this page doesn't offer. */
  hidden?: string[];
  /** Max chain length / max games, for evolution player lists. */
  showEvolutionFilters?: boolean;
  /** PlayStyles to leave out (evolution builder); omitted, no exclude rows. */
  excluded?: {
    playStyles: number[];
    playStylesPlus: number[];
    onChange: (key: "playStyles" | "playStylesPlus", ids: number[]) => void;
  };
};

/** The /players filters on the 2.0 design system, ported from mobile. */
export const PlayerFiltersPanel = ({
  filters,
  activeCount,
  onUpdateFilter,
  onClearFilter,
  onClearAll,
  summary,
  chips,
  hidden = [],
  showEvolutionFilters = false,
  excluded,
}: Props) => {
  const { t } = useTranslation(["players", "common"]);
  const { filterLabel, optionLabel } = useFilterLabels();
  const { countries, leagues, clubs, versions } = useSelector(
    loadedContentSelector,
  );
  const { positions: positionLabels, playerAttributes } = useSelector(
    contentLabelsSelector,
  );
  const playStyleCategoryNames = useSelector(playStyleCategoryNamesSelector);
  const [openAttribute, setOpenAttribute] = useState<string | null>(null);

  const selector = (key: string) =>
    filters.find((f) => f.key === key && f.type === "SELECTOR_FILTER") as
      | SelectorFilterType
      | undefined;
  const range = (key: string) =>
    filters.find((f) => f.key === key && f.type === "RANGE_FILTER") as
      | RangeFilterType
      | undefined;
  const selected = (key: string) => selector(key)?.selectedOptions ?? [];
  const shown = (key: string) =>
    !hidden.includes(key) && filters.some((f) => f.key === key);
  const toggleExcluded =
    (key: "playStyles" | "playStylesPlus") => (id: number) => {
      if (!excluded) return;
      const current = excluded[key];
      excluded.onChange(
        key,
        current.includes(id)
          ? current.filter((x) => x !== id)
          : [...current, id],
      );
    };
  const toggle = (key: string) => (id: number) =>
    onUpdateFilter({ key, value: id });
  // The reducer only writes a bound it is given; null clears it.
  const setMin = (key: string) => (v: number | null) =>
    onUpdateFilter({ key, min: v as number });
  const setMax = (key: string) => (v: number | null) =>
    onUpdateFilter({ key, max: v as number });

  const countryItems = useMemo<DropdownItem[]>(
    () =>
      countries.map((c) => ({
        value: String(c.id),
        label: c.name,
        icon: (
          <img
            className="h-4 w-6 rounded-sm"
            src={asset(`countries/${c.id}.png?scale=50`)}
            alt=""
          />
        ),
      })),
    [countries],
  );
  const leagueItems = useMemo<DropdownItem[]>(
    () =>
      leagues.map((l) => ({
        value: String(l.id),
        label: l.name + (l.isWomen ? " (w)" : ""),
        icon: (
          <img
            className="size-5"
            src={asset(`leagues/dark/${l.id}.png?scale=50`)}
            alt=""
          />
        ),
      })),
    [leagues],
  );
  const clubItems = useMemo<DropdownItem[]>(
    () =>
      clubs.map((c) => ({
        value: String(c.id),
        label: c.name + (c.isWomen ? " (w)" : ""),
        icon: (
          <img
            className="size-5"
            src={asset(`clubs/${c.id}.png?scale=50`)}
            alt=""
          />
        ),
      })),
    [clubs],
  );
  const rarityItems = useMemo<DropdownItem[]>(
    () =>
      versions.map((v) => ({
        value: String(v.id),
        label: v.name,
        icon: (
          <img
            className="h-5"
            src={asset(`cards/e_${v.id}_${v.hasLevels ? 3 : 0}.png`)}
            alt=""
          />
        ),
      })),
    [versions],
  );

  const bodyTypeItems: DropdownItem[] = (
    selector("body-types")?.options ?? []
  ).map((o) => ({ value: String(o.id), label: o.label }));

  const positionFilter = selector("position");
  const positionItems = useMemo<DropdownItem[]>(() => {
    const options = positionFilter?.options ?? [];
    const categories = Array.from(new Set(options.map((o) => o.category)));
    return categories.flatMap((category) => {
      const inCategory = options.filter((o) => o.category === category);
      const zone = positionLabels[inCategory[0]?.label]?.zoneName ?? category;
      return [
        { value: `h-${category}`, label: zone, header: true },
        ...inCategory.map((o) => ({
          value: String(o.id),
          label: positionLabels[o.label]?.name ?? o.label,
        })),
      ];
    });
  }, [positionFilter?.options, positionLabels]);

  const playStyleOptions = selector("playStyles")?.options;
  const playStyleItems = (plus: boolean) => {
    const options = playStyleOptions ?? [];
    const categories = Array.from(new Set(options.map((o) => o.category)));
    return categories.flatMap((category) => [
      {
        value: `h-${category}`,
        label: playStyleCategoryNames[category] ?? category,
        header: true,
      },
      ...options
        .filter((o) => o.category === category)
        .map((o) => ({
          value: String(o.id),
          label: o.label,
          icon: (
            <img
              className="size-5"
              src={asset(
                `${plus ? "playstylesplus" : "playstyles"}/${o.id}.png`,
              )}
              alt=""
            />
          ),
        })),
    ]);
  };
  const playStyleItemsMemo = useMemo(
    () => playStyleItems(false),
    [playStyleOptions, playStyleCategoryNames], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const playStylePlusItemsMemo = useMemo(
    () => playStyleItems(true),
    [playStyleOptions, playStyleCategoryNames], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const segmentOptions = (key: string) =>
    (selector(key)?.options ?? []).map((o) => ({
      id: o.id,
      label: optionLabel(key, o),
    }));

  const rangeRow = (
    key: string,
    icon?: React.ReactNode,
    maxPlaceholder?: string,
    format?: (v: number) => string,
  ) => {
    const f = range(key);
    if (!f || hidden.includes(key)) return null;
    return (
      <ValueRow key={key} icon={icon} label={filterLabel(f)}>
        <RangeInputs
          minValue={f.minValue}
          maxValue={f.maxValue}
          range={f.range}
          maxPlaceholder={maxPlaceholder}
          format={format}
          onChangeMin={setMin(key)}
          onChangeMax={setMax(key)}
        />
      </ValueRow>
    );
  };

  const hasClub = selected("club").length > 0;
  const pastClubsOn = selected("pastAndPresentClubs").length > 0;
  const offBalance = shown("offBalanceDribbling")
    ? range("offBalanceDribbling")
    : undefined;
  const maxOnlyRow = (key: string) => {
    const f = range(key);
    if (!f) return null;
    return (
      <ValueRow key={key} label={filterLabel(f)}>
        <NumberInput
          value={f.maxValue}
          placeholder={String(f.range[1])}
          min={f.range[0]}
          max={f.range[1]}
          onChange={setMax(key)}
        />
      </ValueRow>
    );
  };

  return (
    <div className="flex flex-col">
      {chips ?? (
        <div className="flex items-center justify-between border-b border-edge-strong px-4 py-2">
          <span className="text-xs text-foreground-muted">{summary}</span>
          {activeCount > 0 && (
            <TextButton size="small" onClick={onClearAll}>
              {t("filters.panel.reset")}
            </TextButton>
          )}
        </div>
      )}
      <div className="flex flex-col gap-y-5 p-4 pb-[calc(var(--ad-gutter)+2rem)]">
        {showEvolutionFilters && (
          <FilterCard>
            {maxOnlyRow("max-chain-length")}
            {maxOnlyRow("max-games")}
          </FilterCard>
        )}
        <FilterCard>
          {rangeRow(
            "price",
            <img
              className="size-5"
              src={`${ASSETS}/misc/coin-tmp.png`}
              alt=""
            />,
            t("common:max"),
          )}
          {rangeRow(
            "rating",
            <BarChart3 className="size-4.5 text-foreground-muted" />,
          )}
        </FilterCard>

        <FilterCard>
          {shown("country") && (
            <PickerRow
              label={t("filters.country")}
              items={countryItems}
              selected={selected("country")}
              onToggle={toggle("country")}
            />
          )}
          {shown("league") && (
            <PickerRow
              label={t("filters.league")}
              items={leagueItems}
              selected={selected("league")}
              onToggle={toggle("league")}
            />
          )}
          {shown("club") && (
            <PickerRow
              label={t("filters.club")}
              items={clubItems}
              selected={selected("club")}
              onToggle={toggle("club")}
            />
          )}
          {shown("pastAndPresentClubs") && (
            <NestedToggleRow
              label={t("filters.pastAndPresentClubs.label")}
              hint={
                hasClub
                  ? t("filters.panel.pastClubsHint")
                  : t("filters.panel.pickClubFirst")
              }
              // Off it needs a club first; on, it can always be switched off.
              disabled={!hasClub && !pastClubsOn}
              isActive={pastClubsOn}
              onChange={(on) =>
                on
                  ? onUpdateFilter({ key: "pastAndPresentClubs", value: 1 })
                  : onClearFilter("pastAndPresentClubs")
              }
            />
          )}
          {shown("rarity") && (
            <PickerRow
              label={t("filters.rarity")}
              items={rarityItems}
              selected={selected("rarity")}
              onToggle={toggle("rarity")}
            />
          )}
        </FilterCard>

        <FilterCard>
          {["skillMoves", "weakFoot", "preferredFoot", "gender"].map(
            (key) =>
              shown(key) && (
                <SegmentRow
                  key={key}
                  label={filterLabel(selector(key))}
                  options={segmentOptions(key)}
                  selected={selected(key)}
                  onToggle={toggle(key)}
                />
              ),
          )}
          {rangeRow("height", undefined, undefined, cmToFeetAndInches)}
        </FilterCard>

        <FilterCard>
          {shown("body-types") && (
            <PickerRow
              label={t("filters.bodyType")}
              items={bodyTypeItems}
              selected={selected("body-types")}
              onToggle={toggle("body-types")}
              hideSearch
            />
          )}
          {shown("playStyles") && (
            <PickerRow
              label={t("filters.playStyle")}
              items={playStyleItemsMemo}
              selected={selected("playStyles")}
              onToggle={toggle("playStyles")}
            />
          )}
          {shown("playStylesPlus") && (
            <PickerRow
              label={t("filters.playStylePlus")}
              items={playStylePlusItemsMemo}
              selected={selected("playStylesPlus")}
              onToggle={toggle("playStylesPlus")}
            />
          )}
          {excluded && (
            <PickerRow
              label={t("filters.panel.excludePlayStyles")}
              items={playStyleItemsMemo}
              selected={excluded.playStyles}
              onToggle={toggleExcluded("playStyles")}
            />
          )}
          {excluded && (
            <PickerRow
              label={t("filters.panel.excludePlayStylesPlus")}
              items={playStylePlusItemsMemo}
              selected={excluded.playStylesPlus}
              onToggle={toggleExcluded("playStylesPlus")}
            />
          )}
          {shown("position") && (
            <PickerRow
              label={t("filters.position")}
              items={positionItems}
              selected={selected("position")}
              onToggle={toggle("position")}
              hideSearch
            />
          )}
        </FilterCard>

        <FilterCard>
          {shown("accelerates") && (
            <SegmentRow
              label={t("filters.accelerates.header")}
              options={segmentOptions("accelerates")}
              selected={selected("accelerates")}
              onToggle={toggle("accelerates")}
            />
          )}
          {offBalance && (
            <ValueRow
              label={
                <span className="flex items-center gap-x-1.5">
                  {filterLabel(offBalance)}
                  <span
                    className="flex text-foreground-muted"
                    title={t("filters.offBalanceDribblingExplainer")}
                  >
                    <Info className="size-3.5" />
                  </span>
                </span>
              }
            >
              <RangeInputs
                minValue={offBalance.minValue}
                maxValue={offBalance.maxValue}
                range={offBalance.range}
                onChangeMin={setMin("offBalanceDribbling")}
                onChangeMax={setMax("offBalanceDribbling")}
              />
            </ValueRow>
          )}
          {rangeRow("maxPlayStyles")}
          {rangeRow("maxPlayStylesPlus")}
        </FilterCard>

        <FilterCard>
          {inGameAttributeCategories.map((category) => {
            const key = categoryToUniqueKey[category];
            const f = range(key);
            if (!f) return null;
            const subs = Object.entries(inGameAttributesDict).filter(
              ([, a]) => a.category === category && a.label !== category,
            );
            const isOpen = openAttribute === category;
            const anySet = [key, ...subs.map(([k]) => k)].some((k) => {
              const r = range(k);
              return r && (r.minValue != null || r.maxValue != null);
            });
            return (
              <div key={category} className="flex flex-col">
                <ValueRow label={playerAttributes[key]?.label ?? category}>
                  <RangeInputs
                    minValue={f.minValue}
                    maxValue={f.maxValue}
                    range={f.range}
                    onChangeMin={setMin(key)}
                    onChangeMax={setMax(key)}
                  />
                  <ExpandButton
                    open={isOpen}
                    active={anySet}
                    onClick={() => setOpenAttribute(isOpen ? null : category)}
                  />
                </ValueRow>
                {isOpen && (
                  <div className="mx-3.5 mb-3 flex flex-col gap-y-1.5 rounded-xl border border-edge-strong bg-surface-raised p-2.5">
                    {subs.map(([subKey, a]) => {
                      const sub = range(subKey);
                      if (!sub) return null;
                      return (
                        <div key={subKey} className="flex items-center gap-x-2">
                          <span className="flex-1 text-xs text-foreground-muted">
                            {playerAttributes[subKey]?.label ?? a.label}
                          </span>
                          <RangeInputs
                            minValue={sub.minValue}
                            maxValue={sub.maxValue}
                            range={sub.range}
                            onChangeMin={setMin(subKey)}
                            onChangeMax={setMax(subKey)}
                          />
                          <div className="w-8" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </FilterCard>
      </div>
    </div>
  );
};
